import { useCallback, useEffect, useMemo, useState } from "react";
import client from "../api/client";

const emptyLine = {
  itemId: "",
  itemName: "",
  hsnCode: "",
  unit: "Nos",
  qty: 1,
  rate: 0,
  discountAmount: 0,
  gstPercent: 18,
};

const fmt = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n.toFixed(2) : "0.00";
};

const styles = {
  page: {
    fontSize: 11,
    height: "calc(100vh - 20px)",
    overflowY: "auto",
    overflowX: "hidden",
    padding: 0,
  },
  card: {
    border: 0,
    borderRadius: 14,
    overflow: "hidden",
    boxShadow: "0 2px 10px rgba(0,0,0,.08)",
    background: "#fff",
  },
  header: {
    background: "linear-gradient(90deg, #22052D, #CCB3D1)",
    color: "#fff",
    padding: "8px 12px",
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  title: { fontSize: 13, fontWeight: 700, margin: 0 },
  sub: { fontSize: 10, opacity: 0.85 },
  body: { padding: 10 },
  label: { fontSize: 10, fontWeight: 700, marginBottom: 3, color: "#495057" },
  input: { height: 30, fontSize: 11, borderRadius: 7 },
  btn: {
    height: 28,
    fontSize: 11,
    borderRadius: 7,
    fontWeight: 600,
    padding: "3px 9px",
  },
  info: {
    fontSize: 11,
    padding: "7px 10px",
    borderRadius: 10,
    background: "#f8f9fa",
    border: "1px solid #e9ecef",
  },
  tableWrap: {
    maxHeight: 300,
    overflowY: "auto",
    overflowX: "auto",
    border: "1px solid #e9ecef",
    borderRadius: 10,
  },
  table: { fontSize: 11, minWidth: 1250, marginBottom: 0 },
  th: {
    fontSize: 10,
    padding: "7px 8px",
    whiteSpace: "nowrap",
    color: "#fff",
    position: "sticky",
    top: 0,
    zIndex: 1,
  },
  td: {
    fontSize: 11,
    padding: "5px 6px",
    verticalAlign: "middle",
    whiteSpace: "nowrap",
  },
  summaryTh: { fontSize: 11, padding: "6px 8px", background: "#f8f9fa" },
  summaryTd: { fontSize: 11, padding: "6px 8px" },
};

export default function SalesVoucherPage() {
  const [customers, setCustomers] = useState([]);
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(false);

  const [form, setForm] = useState({
    invoiceNo: "",
    invoiceDate: new Date().toISOString().split("T")[0],
    customerId: "",
    saleType: "CREDIT",
    supplyType: "INTRA",
    discountAmount: 0,
    notes: "",
    details: [{ ...emptyLine }],
  });

  useEffect(() => {
    loadMasters();
  }, []);

  const loadMasters = async () => {
    try {
      setLoading(true);
      const [customerRes, itemRes] = await Promise.all([
        client.get("/sales/customers"),
        client.get("/inventory/items"),
      ]);
      setCustomers(Array.isArray(customerRes.data) ? customerRes.data : []);
      setItems(Array.isArray(itemRes.data) ? itemRes.data : []);
    } catch {
      alert("Failed to load customers/items");
    } finally {
      setLoading(false);
    }
  };

  const calculateLine = useCallback(
    (line) => {
      const qty = Number(line?.qty || 0);
      const rate = Number(line?.rate || 0);
      const discountAmount = Number(line?.discountAmount || 0);
      const gstPercent = Number(line?.gstPercent || 0);
      const taxableValue = Math.max(qty * rate - discountAmount, 0);
      const gstAmount = (taxableValue * gstPercent) / 100;

      return {
        taxableValue,
        cgst: form.supplyType === "INTRA" ? gstAmount / 2 : 0,
        sgst: form.supplyType === "INTRA" ? gstAmount / 2 : 0,
        igst: form.supplyType === "INTER" ? gstAmount : 0,
        lineTotal: taxableValue + gstAmount,
      };
    },
    [form.supplyType],
  );

  const updateLine = (index, field, value) => {
    const details = [...form.details];
    details[index] = { ...details[index], [field]: value };

    if (field === "itemId") {
      const item = items.find((x) => String(x.Id ?? x.id) === String(value));
      if (item) {
        details[index].itemName = item.ItemName ?? item.itemName ?? "";
        details[index].hsnCode = item.HSNCode ?? item.hsnCode ?? "";
        details[index].unit = item.Unit ?? item.unit ?? "Nos";
        details[index].rate = Number(item.SalePrice ?? item.salePrice ?? 0);
        details[index].gstPercent = Number(
          item.GSTPercent ?? item.gstPercent ?? item.TaxPercent ?? 18,
        );
      }
    }

    setForm((prev) => ({ ...prev, details }));
  };

  const addRow = () =>
    setForm((prev) => ({
      ...prev,
      details: [...prev.details, { ...emptyLine }],
    }));

  const removeRow = (index) => {
    if (form.details.length === 1) return alert("At least one row is required");
    setForm((prev) => ({
      ...prev,
      details: prev.details.filter((_, i) => i !== index),
    }));
  };

  const totals = useMemo(() => {
    return form.details.reduce(
      (acc, line) => {
        const c = calculateLine(line);
        acc.subTotal += c.taxableValue;
        acc.cgst += c.cgst;
        acc.sgst += c.sgst;
        acc.igst += c.igst;
        acc.taxAmount += c.cgst + c.sgst + c.igst;
        acc.grandTotal += c.lineTotal;
        return acc;
      },
      { subTotal: 0, cgst: 0, sgst: 0, igst: 0, taxAmount: 0, grandTotal: 0 },
    );
  }, [form.details, calculateLine]);

  const grandTotal = totals.grandTotal - Number(form.discountAmount || 0);

  const save = async () => {
    if (!form.invoiceNo.trim()) return alert("Invoice No is required");
    if (!form.customerId) return alert("Customer is required");

    for (let i = 0; i < form.details.length; i++) {
      const row = form.details[i];
      if (!row.itemId) return alert(`Select item in row ${i + 1}`);
      if (Number(row.qty || 0) <= 0)
        return alert(`Qty must be greater than 0 in row ${i + 1}`);
      if (Number(row.rate || 0) <= 0)
        return alert(`Rate must be greater than 0 in row ${i + 1}`);
    }

    const payload = {
      companyId: Number(localStorage.getItem("companyId") || 0),
      invoiceNo: form.invoiceNo.trim(),
      invoiceDate: form.invoiceDate,
      customerId: Number(form.customerId),
      saleType: form.saleType,
      supplyType: form.supplyType,
      discountAmount: Number(form.discountAmount || 0),
      notes: form.notes,
      details: form.details.map((x) => ({
        itemId: Number(x.itemId),
        itemName: x.itemName || "",
        hsnCode: x.hsnCode || "",
        unit: x.unit || "Nos",
        qty: Number(x.qty || 0),
        rate: Number(x.rate || 0),
        discountAmount: Number(x.discountAmount || 0),
        gstPercent: Number(x.gstPercent || 0),
      })),
    };

    try {
      setSaving(true);
      await client.post("/sales/invoices", payload);
      alert("Invoice saved and journal posted successfully");

      setForm({
        invoiceNo: "",
        invoiceDate: new Date().toISOString().split("T")[0],
        customerId: "",
        saleType: "CREDIT",
        supplyType: "INTRA",
        discountAmount: 0,
        notes: "",
        details: [{ ...emptyLine }],
      });
    } catch (error) {
      alert(
        error?.response?.data?.message ||
          error?.response?.data ||
          "Failed to save invoice",
      );
    } finally {
      setSaving(false);
    }
  };

  const selectedCustomer = customers.find(
    (c) => String(c.Id ?? c.id) === String(form.customerId),
  );

  return (
    <div className="container-fluid py-2" style={styles.page}>
      <div style={styles.card}>
        <div style={styles.header}>
          <div>
            <h6 style={styles.title}>
              <i className="bi bi-cart-check me-2"></i>
              GST Sales Voucher / Invoice
            </h6>
            <div style={styles.sub}>
              Create GST invoice with auto journal posting
            </div>
          </div>
          <span className="badge bg-light text-dark" style={{ fontSize: 10 }}>
            {form.supplyType === "INTRA" ? "CGST + SGST" : "IGST"}
          </span>
        </div>

        <div style={styles.body}>
          {loading && (
            <div
              className="alert alert-info"
              style={{ fontSize: 11, padding: "7px 10px" }}
            >
              Loading masters...
            </div>
          )}

          <div className="row g-2 mb-2">
            <Field label="Invoice No" className="col-md-2">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.invoiceNo}
                onChange={(e) =>
                  setForm({ ...form, invoiceNo: e.target.value })
                }
              />
            </Field>

            <Field label="Date" className="col-md-2">
              <input
                type="date"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.invoiceDate}
                onChange={(e) =>
                  setForm({ ...form, invoiceDate: e.target.value })
                }
              />
            </Field>

            <Field label="Customer" className="col-md-3">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.customerId}
                onChange={(e) =>
                  setForm({ ...form, customerId: e.target.value })
                }
              >
                <option value="">Select Customer</option>
                {customers.map((c) => (
                  <option key={c.Id ?? c.id} value={c.Id ?? c.id}>
                    {(c.CustomerCode ?? c.customerCode ?? "") +
                      " - " +
                      (c.CustomerName ?? c.customerName ?? "")}
                  </option>
                ))}
              </select>
            </Field>

            <Field label="Sale Type" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.saleType}
                onChange={(e) => setForm({ ...form, saleType: e.target.value })}
              >
                <option value="CASH">Cash</option>
                <option value="CREDIT">Credit</option>
              </select>
            </Field>

            <Field label="Supply" className="col-md-2">
              <select
                className="form-select form-select-sm"
                style={styles.input}
                value={form.supplyType}
                onChange={(e) =>
                  setForm({ ...form, supplyType: e.target.value })
                }
              >
                <option value="INTRA">Intra State</option>
                <option value="INTER">Inter State</option>
              </select>
            </Field>

            <Field label="Disc." className="col-md-1">
              <input
                type="number"
                className="form-control form-control-sm"
                style={styles.input}
                value={form.discountAmount}
                onChange={(e) =>
                  setForm({ ...form, discountAmount: e.target.value })
                }
              />
            </Field>
          </div>

          <div style={styles.info} className="mb-2">
            <strong>Customer:</strong>{" "}
            {selectedCustomer?.CustomerName ??
              selectedCustomer?.customerName ??
              "-"}{" "}
            | <strong>GSTIN:</strong>{" "}
            {selectedCustomer?.GSTNo ?? selectedCustomer?.gstNo ?? "-"} |{" "}
            <strong>Sale Type:</strong> {form.saleType}
          </div>

          <ItemsTable
            items={items}
            details={form.details}
            calculateLine={calculateLine}
            updateLine={updateLine}
            removeRow={removeRow}
          />

          <button
            className="btn btn-outline-primary btn-sm mb-2"
            style={styles.btn}
            onClick={addRow}
          >
            <i className="bi bi-plus-circle me-1"></i>Add Row
          </button>

          <div className="row g-2">
            <Field label="Notes" className="col-md-8">
              <input
                className="form-control form-control-sm"
                style={styles.input}
                value={form.notes}
                onChange={(e) => setForm({ ...form, notes: e.target.value })}
                placeholder="Example: Enter purpose, reference bill/invoice no., payment terms or adjustment reason"
              />
            </Field>

            <div className="col-md-4">
              <table className="table table-bordered mb-2">
                <tbody>
                  {[
                    ["Taxable Value", totals.subTotal],
                    ["CGST", totals.cgst],
                    ["SGST", totals.sgst],
                    ["IGST", totals.igst],
                    ["Total GST", totals.taxAmount],
                    ["Header Discount", form.discountAmount],
                    ["Grand Total", grandTotal],
                  ].map(([k, v]) => (
                    <tr
                      key={k}
                      className={
                        k === "Grand Total" ? "table-light fw-bold" : ""
                      }
                    >
                      <th style={styles.summaryTh}>{k}</th>
                      <td className="text-end" style={styles.summaryTd}>
                        ₹ {fmt(v)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <button
                className="btn btn-success btn-sm w-100"
                style={{ ...styles.btn, height: 32 }}
                disabled={saving}
                onClick={save}
              >
                <i className="bi bi-check2-circle me-1"></i>
                {saving ? "Saving..." : "Save Invoice"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Field({ label, className, children }) {
  return (
    <div className={className}>
      <label style={styles.label}>{label}</label>
      {children}
    </div>
  );
}

function ItemsTable({ items, details, calculateLine, updateLine, removeRow }) {
  return (
    <div className="mb-2" style={styles.tableWrap}>
      <table
        className="table table-bordered table-hover align-middle"
        style={styles.table}
      >
        <thead>
          <tr>
            {[
              ["Item", 190],
              ["HSN", 90],
              ["Unit", 70],
              ["Qty", 70],
              ["Rate", 80],
              ["Line Disc.", 90],
              ["GST %", 70],
              ["Taxable", 90],
              ["CGST", 80],
              ["SGST", 80],
              ["IGST", 80],
              ["Total", 90],
              ["Action", 70],
            ].map(([h, w]) => (
              <th
                key={h}
                style={{ ...styles.th, minWidth: w }}
                className={
                  ["Taxable", "CGST", "SGST", "IGST", "Total"].includes(h)
                    ? "text-end"
                    : ""
                }
              >
                {h}
              </th>
            ))}
          </tr>
        </thead>

        <tbody>
          {details.map((line, index) => {
            const calc = calculateLine(line);

            return (
              <tr key={index}>
                <td style={styles.td}>
                  <select
                    className="form-select form-select-sm"
                    style={styles.input}
                    value={line.itemId}
                    onChange={(e) =>
                      updateLine(index, "itemId", e.target.value)
                    }
                  >
                    <option value="">Select Item</option>
                    {items.map((item) => (
                      <option
                        key={item.Id ?? item.id}
                        value={item.Id ?? item.id}
                      >
                        {(item.ItemCode ?? item.itemCode ?? "") +
                          " - " +
                          (item.ItemName ?? item.itemName ?? "")}
                      </option>
                    ))}
                  </select>
                </td>

                {[
                  "hsnCode",
                  "unit",
                  "qty",
                  "rate",
                  "discountAmount",
                  "gstPercent",
                ].map((field) => (
                  <td style={styles.td} key={field}>
                    <input
                      type={
                        [
                          "qty",
                          "rate",
                          "discountAmount",
                          "gstPercent",
                        ].includes(field)
                          ? "number"
                          : "text"
                      }
                      className="form-control form-control-sm"
                      style={styles.input}
                      value={line[field]}
                      onChange={(e) => updateLine(index, field, e.target.value)}
                    />
                  </td>
                ))}

                <td className="text-end" style={styles.td}>
                  {fmt(calc.taxableValue)}
                </td>
                <td className="text-end" style={styles.td}>
                  {fmt(calc.cgst)}
                </td>
                <td className="text-end" style={styles.td}>
                  {fmt(calc.sgst)}
                </td>
                <td className="text-end" style={styles.td}>
                  {fmt(calc.igst)}
                </td>
                <td className="text-end fw-bold" style={styles.td}>
                  {fmt(calc.lineTotal)}
                </td>

                <td style={styles.td}>
                  <button
                    className="btn btn-danger btn-sm"
                    style={{ ...styles.btn, height: 25, padding: "2px 8px" }}
                    onClick={() => removeRow(index)}
                  >
                    <i className="bi bi-x-lg"></i>
                  </button>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
